import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IEmployeesGroup } from 'types/income';
import { TableDataResponse } from 'types/general';
import {
  exportXlsxConfig,
  getSorting,
  mergeOrgStructureIds,
} from 'utils/general';
import { EmployeeGroupsFilters } from './employee-groups.types';

const getFilters = (filters: Partial<EmployeeGroupsFilters>) => {
  const orgStructure = mergeOrgStructureIds(filters);

  return {
    page: filters.page,
    pageSize: filters.pageSize,
    ...getSorting(filters.sort),
    filter: {
      name: filters.name || undefined,
      groupIds: filters.employeeGroups,
      benefitPlanIds: filters.benefits,
      companyIds: orgStructure.companies,
      departmentIds: orgStructure.departments,
      teamIds: orgStructure.teams,
      employeeStatus: filters.employeeStatus,
      benefitStatus: filters.benefitStatus,
    },
  };
};

interface GetEmployeeGroups {
  filters: Partial<EmployeeGroupsFilters>;
}

interface GetEmployeeGroup {
  employeeGroupId: string;
}

interface CreateEmployeeGroup {
  name: string;
}

interface UpdateEmployeeGroup {
  employeeGroupId: string;
  data: Partial<CreateEmployeeGroup>;
}

interface CopyEmployeeGroup {
  employeeGroupId: string;
  name: string;
}

interface DeleteEmployeeGroup {
  employeeGroupId: string;
}

interface ExportXls {
  filters: Partial<EmployeeGroupsFilters>;
}

export const loaders = {
  getEmployeeGroups: ({ filters }: GetEmployeeGroups) => {
    return api<TableDataResponse<IEmployeesGroup>>({
      url: Endpoints.clientAdmin.employeeGroups.root,
      version: 'v2',
      params: {
        ...getFilters(filters),
      },
    });
  },
  getEmployeeGroup: ({ employeeGroupId }: GetEmployeeGroup) => {
    return api<IEmployeesGroup>({
      url: Endpoints.clientAdmin.employeeGroups.byId.root.replace(
        ':employeeGroupId',
        employeeGroupId,
      ),
    });
  },
  createEmployeeGroup: ({ name }: CreateEmployeeGroup) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employeeGroups.root,
      data: {
        name,
      },
      method: 'post',
    });
  },
  updateEmployeeGroup: ({ employeeGroupId, data }: UpdateEmployeeGroup) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employeeGroups.byId.root.replace(
        ':employeeGroupId',
        employeeGroupId,
      ),
      data,
      method: 'patch',
    });
  },
  copyEmployeeGroup: ({ employeeGroupId, name }: CopyEmployeeGroup) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employeeGroups.byId.copy.replace(
        ':employeeGroupId',
        employeeGroupId,
      ),
      data: { name },
      method: 'post',
    });
  },
  deleteEmployeeGroup: ({ employeeGroupId }: DeleteEmployeeGroup) => {
    return api({
      url: Endpoints.clientAdmin.employeeGroups.byId.root.replace(
        ':employeeGroupId',
        employeeGroupId,
      ),
      method: 'delete',
    });
  },
  exportXls: ({ filters }: ExportXls) => {
    const { page, pageSize, sorting, direction, ...restFilters } =
      getFilters(filters);
    return api({
      url: Endpoints.clientAdmin.employeeGroups.export.xls,
      version: 'v2',
      params: {
        ...restFilters,
      },
      ...exportXlsxConfig,
    });
  },
};
